if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.upsellVariantId = this.dataset.upsellVariantId;

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      // Helper: Sync Releasit quantity before form submission
      syncReleasitQuantity() {
        let quantity = 1;

        // Priority 0: Get existing quantity from form (supports external inputs via 'form' attribute)
        const existingQtyInput = this.form.elements['quantity'];
        if (existingQtyInput) {
           const val = parseInt(existingQtyInput.value, 10);
           if (!isNaN(val) && val > 0) {
              quantity = val;
           }
        }

        // Priority 1: Look for RSI_QUANTITY pattern in hidden inputs
        const rsiInputs = document.querySelectorAll(
          'input[name*="RSI_QUANTITY"], input[id*="RSI_QUANTITY"]'
        );
        rsiInputs.forEach((input) => {
          const val = parseInt(input.value, 10);
          if (val && val > 0) {
            quantity = val;
          }
        });

        // Priority 2: Look for checked radio buttons with quantity text
        const releasitContainers = document.querySelectorAll(
          '[class*="releasit"], [data-releasit-form], [id*="releasit"]'
        );
        releasitContainers.forEach((container) => {
          const checkedInputs = container.querySelectorAll(
            'input[type="radio"]:checked, input[type="checkbox"]:checked'
          );
          checkedInputs.forEach((input) => {
            const text =
              input.closest("label")?.textContent || input.value || "";
            const match = text.match(/(?:Achetez\s*)?(\d+)\s*article/i);
            if (match) {
              const qty = parseInt(match[1], 10);
              if (qty > quantity) quantity = qty;
            }
          });
        });

        // CRITICAL: Remove duplicate quantity inputs to prevent adding multiple values
        if (this.form && quantity > 0) {
          // We only clean up INTERNAL duplicates to avoid interfering with external ones
          const allQtyInputs = this.form.querySelectorAll(
            'input[name="quantity"]'
          );

          // Remove duplicates, keep only the first one
          if (allQtyInputs.length > 1) {
            for (let i = 1; i < allQtyInputs.length; i++) {
              allQtyInputs[i].remove();
            }
          }

          // Update the single remaining input or create one if we strictly need to override
          // However, if we have an external input (existingQtyInput) and no internal one, 
          // we might not want to create an internal one if the external one is sufficient.
          // But to be safe and consistent with previous logic:
          
          const qtyInput = this.form.querySelector('input[name="quantity"]');
          if (qtyInput) {
            qtyInput.value = quantity;
          } else if (!existingQtyInput) {
            // Only create new internal input if NO input exists (internal or external)
            const newQtyInput = document.createElement("input");
            newQtyInput.type = "hidden";
            newQtyInput.name = "quantity";
            newQtyInput.value = quantity;
            this.form.appendChild(newQtyInput);
          } else {
             // If external input exists, update it
             existingQtyInput.value = quantity;
          }

          // Also check quantity-input component outside form (if linked via form attribute)
          // This covers cases where standard quantity-input is used
          const quantityInputComponent = document.querySelector(
            'quantity-input input[name="quantity"]'
          );
          if (
            quantityInputComponent &&
            quantityInputComponent.getAttribute("form") === this.form.id
          ) {
            quantityInputComponent.value = quantity;
          }
        }

        return quantity;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        // Mark this cart add as expected (for cart protection system)
        if (window.cartProtection && typeof window.cartProtection.markAsExpected === 'function') {
          window.cartProtection.markAsExpected();
          console.log('🛡️ Cart add marked as expected (user-initiated)');
        }

        this.handleErrorMessage();

        // CRITICAL: Sync Releasit quantity BEFORE creating FormData
        const releasitQty = this.syncReleasitQuantity();
        if (releasitQty > 1) {
          console.log(
            "🔄 Product-form.js: Synced Releasit quantity:",
            releasitQty
          );
        }

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        // Double-check: Ensure quantity is correct in FormData
        const formQuantity = formData.get("quantity");
        if (releasitQty > 1 && parseInt(formQuantity) !== releasitQty) {
          formData.set("quantity", releasitQty);
          console.log(
            "🔧 Product-form.js: Fixed quantity in FormData:",
            releasitQty
          );
        }
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.skipCart = false;
        if (
          this.form.querySelector("[name=id]").dataset.skipCart &&
          this.form.querySelector("[name=id]").dataset.skipCart === "true"
        ) {
          this.skipCart = true;
        }

        const variantId = formData.get("id");

        // CRITICAL: Check if product already exists in cart to prevent quantity accumulation
        fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cartState) => {
            // Check if variant already exists in cart
            const existingItem = cartState.items.find(
              (item) => item.variant_id === parseInt(variantId)
            );

            if (existingItem) {
              // Product exists in cart - use cart/change.js to SET quantity instead of adding
              const targetQuantity = releasitQty; // Use Releasit quantity directly

              console.log(
                `🔄 Product exists in cart (key: ${existingItem.key}, current qty: ${existingItem.quantity}). Setting to: ${targetQuantity}`
              );

              const changeConfig = fetchConfig("javascript");
              changeConfig.headers["X-Requested-With"] = "XMLHttpRequest";
              changeConfig.headers["Content-Type"] = "application/json";

              const changeBody = JSON.stringify({
                id: existingItem.key, // Use item key for cart/change.js
                quantity: targetQuantity,
                sections: this.cart
                  ? this.cart.getSectionsToRender().map((section) => section.id)
                  : [],
                sections_url: window.location.pathname,
              });

              changeConfig.body = changeBody;

              // Skip upsell if product exists
              return fetch(`${routes.cart_change_url}`, changeConfig);
            } else {
              // Product doesn't exist - proceed with normal add flow
              if (cartState.item_count === 0 && this.upsellVariantId) {
                return fetch(
                  `${routes.cart_add_url}`,
                  this.getUpsellProductConfig()
                );
              }
              // Then add the product
              return fetch(`${routes.cart_add_url}`, config);
            }
          })
          .then((response) => {
            // Handle response from either cart/add or cart/change
            if (!response) {
              // Fallback: if somehow no response, try adding
              return fetch(`${routes.cart_add_url}`, config).then((res) =>
                res.json()
              );
            }
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (this.skipCart) {
              window.location = "/checkout";
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
            
            // REAL-TIME cart count update
            if (!this.error && response) {
              this.updateCartCountImmediately(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");

            // Dispatch custom event for external apps (like Releasit COD Form)
            document.dispatchEvent(
              new CustomEvent("product-form:add-to-cart-complete", {
                detail: {
                  formId: this.form.id,
                  variantId: this.form.querySelector("[name=id]")?.value,
                  success: !this.error,
                },
              })
            );
          });
      }
      
      updateCartCountImmediately(response) {
        // Get cart count from response
        const cartCount = response.item_count || response.items?.length || 0;
        
        // Update all possible cart count elements
        const cartCountSelectors = [
          '.cart-count-bubble span[aria-hidden="true"]',
          '.cart-count-bubble span',
          '.cart-count-bubble',
          '[data-cart-count]',
          '.header__icon--cart .count-bubble',
          '#cart-icon-bubble span',
          '.cart-icon-bubble span'
        ];
        
        cartCountSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((el) => {
            if (el) {
              el.textContent = cartCount;
              el.setAttribute('data-cart-count', cartCount);
              
              // Show/hide based on count
              if (cartCount > 0) {
                el.classList.remove('hidden');
                el.style.display = '';
              }
            }
          });
        });
        
        console.log('🛒 Cart count updated in real-time:', cartCount);
      }
      
      getUpsellProductConfig() {
        const upsellFormData = new FormData();
        upsellFormData.append("id", this.upsellVariantId);
        upsellFormData.append("quantity", 1);

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        config.body = upsellFormData;

        return config;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
